// storage.ts — a defensive key/value wrapper around localStorage.
//
// iOS/Android webviews can throw on storage access (private mode, quota,
// disabled cookies). We never let that crash the app: on failure we fall back
// to an in-memory map for the session. This keeps progress "privacy-conscious
// and local" while staying robust inside Capacitor.

const memory = new Map<string, string>();

function available(): boolean {
  try {
    const k = '__rn_probe__';
    window.localStorage.setItem(k, '1');
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

const hasLocalStorage = typeof window !== 'undefined' && available();

export const storage = {
  get(key: string): string | null {
    try {
      return hasLocalStorage ? window.localStorage.getItem(key) : memory.get(key) ?? null;
    } catch {
      return memory.get(key) ?? null;
    }
  },
  set(key: string, value: string): void {
    try {
      if (hasLocalStorage) window.localStorage.setItem(key, value);
      else memory.set(key, value);
    } catch {
      memory.set(key, value);
    }
  },
  remove(key: string): void {
    try {
      if (hasLocalStorage) window.localStorage.removeItem(key);
      else memory.delete(key);
    } catch {
      memory.delete(key);
    }
  },
  getJSON<T>(key: string, fallback: T): T {
    const raw = this.get(key);
    if (raw == null) return fallback;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  },
  setJSON(key: string, value: unknown): void {
    this.set(key, JSON.stringify(value));
  },
};
